import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import OurServices from "./components/OurServices";
import AboutUs from "./components/AboutUs";
import HowWeHelp from "./components/HowWeHelp";
import TeamMessage from "./components/TeamMessage";
import OurProcess from "./components/OurProcess";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import ScrollAnimations from "./components/ScrollAnimations";
import FloatingContactButton from "./components/FloatingContactButton";
import ElectricalSpares from "./pages/ElectricalSpares";
import Instrumentation from "./pages/Instrumentation";
import OwsOdme from "./pages/OwsOdme";
import FireSmoke from "./pages/FireSmoke";
import { SITE } from "./constants/siteConfig";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);
  return null;
}

function App() {
  // Structured Data for SEO: Marine Sealink B.V. (marine spare parts supplier)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE.name,
    "legalName": SITE.name,
    "description": SITE.description,
    "slogan": SITE.tagline,
    "url": SITE.url,
    "logo": `${SITE.url}${SITE.logo}`,
    "vatID": SITE.vat,
    "identifier": {
      "@type": "PropertyValue",
      "propertyID": "KVK",
      "value": SITE.kvk
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+31687334469",
      "contactType": "sales",
      "email": SITE.email,
      "areaServed": "Worldwide"
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Rotterdam",
      "addressCountry": "NL"
    },
    "sameAs": [
      SITE.whatsapp
    ],
    "makesOffer": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Marine Electrical Spares",
          "description": "Marine-approved electrical components for power management and distribution: contactors, thermal overloads, relays, MCCBs, MCBs, ACBs, circuit breakers, VFDs, soft starters, AVRs, marine cables, fuses and busbars from Schneider, ABB, Siemens, and Terasaki."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Marine Instrumentation & Control Spares",
          "description": "Precision measurement sensors and automation controls for engine room operations: pressure & temperature transmitters, RTDs, thermocouples, thermowells, gauges, digital calibrators, level switches, ultrasonic flow meters, pneumatic positioners, I/P converters, PID controllers and signal isolators from Danfoss, WIKA, Emerson, and Yokogawa."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "OWS & ODME Spares",
          "description": "Certified spares for MARPOL environmental compliance: 15ppm bilge alarms, content monitors, oil content meters, measuring cells, solenoid valves, sample pumps, filters, DP transmitters, flow meters, coalescers, filter elements, logic boards and display modules from Deckma Hamburg, Rivertrace, and JOWA."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Marine Fire & Smoke Detection Spares",
          "description": "Safety replacements for marine addressable and conventional alarm systems: optical, ionization and duct smoke detectors, addressable heat and UV/IR flame detectors, manual call points, alarm bells, intrinsically safe barriers and isolators, gas detection sensors, test aerosols and gases from Consilium, Salwico, Autronica, and Apollo."
        }
      }
    ]
  };

  return (
    <Router>
      <ScrollToTop />
      <ScrollAnimations />
      <FloatingContactButton />
      {/* Structured Data Script */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Routes>
        <Route
          path="/"
          element={
            <div className="page-transition">
              <Navbar />
              <HeroSection />
              <div className="fade-in-section">
                <OurServices />
              </div>
              <div className="fade-in-section">
                <AboutUs />
              </div>
              <div className="fade-in-section">
                <HowWeHelp />
              </div>
              <div className="fade-in-section">
                <TeamMessage />
              </div>
              <div className="fade-in-section">
                <OurProcess />
              </div>
              <div className="fade-in-section">
                <Contact />
              </div>
              <Footer />
            </div>
          }
        />
        <Route path="/electrical-spares" element={<ElectricalSpares />} />
        <Route path="/instrumentation" element={<Instrumentation />} />
        <Route path="/ows-odme" element={<OwsOdme />} />
        <Route path="/fire-smoke" element={<FireSmoke />} />

        {/* Redirects from retired services (marine internet, Starlink, cybersecurity, old spares hub) */}
        <Route path="/marine-internet" element={<Navigate to="/" replace />} />
        <Route path="/cybersecurity" element={<Navigate to="/" replace />} />
        <Route path="/marine-spares" element={<Navigate to="/" replace />} />

        {/* Catch-all → home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
